<template>
    <EChart
        :options="chartOptions"
        card
        :header="$t('priceRangeAndDeviations')"
        :loading="loading"
        :tooltip="$t('chartHeaderTooltip')"
        class="shadow-none m-0"
    />
</template>



<script>
    import EChart from '@/components/common/EChart.vue';

    export default {
        name: 'PriceRangeAndDeviations',
        components: {
            EChart,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestReportSet: this.parentComponent.requestReportSet,
                requestReport: this.parentComponent.requestReport,
            };
        },
        computed: {
            timeRange() {
                return this.parentComponent.timeRange;
            },
            loading() {
                return this.parentComponent.loading;
            },
            items() {
                return this.parentComponent.items || [];
            },
            itemsCompare() {
                return this.parentComponent.itemsCompare || [];
            },
            chartOptions() {
                // 1) Filtr bezpieczeństwa – tylko rekordy z kompletnymi liczbami
                const safeRows = (this.items || []).filter(item => {
                    const values = [item.priceRangeMin, item.priceRangeMax, item.minPrice, item.maxPrice];
                    return values.every(val => val !== null && val !== undefined && Number.isFinite(Number(val)));
                });

                if (!safeRows.length) {
                    return { series: [] };
                }

                // 1a) Mapa rekordów porównawczych (klucz: id -> sku -> name)
                const keyOf = row => row && (row.id ?? row.sku ?? row.name);
                const compareMap = new Map();
                (this.itemsCompare || []).forEach(row => {
                    const key = keyOf(row);
                    if (key !== undefined && key !== null) compareMap.set(key, row);
                });

                // 1b) Helpery formatowania
                const fc = this.$format.currency;
                const fmtPct = this.$format.percent;

                const diffTag = (currentValue, previousValue) => {
                    const curr = Number(currentValue);
                    const prev = Number(previousValue);
                    const hasPrev = Number.isFinite(prev) && prev !== 0;
                    const hasCurr = Number.isFinite(curr);

                    if (!hasCurr || !Number.isFinite(prev)) {
                        return '<span style="margin-left:5px;color:#999">—</span>';
                    }
                    if (!hasPrev) {
                        // brak sensownej bazy odniesienia (0 lub NaN) – unikamy dzielenia przez 0
                        return '<span style="margin-left:5px;color:#999">—</span>';
                    }

                    const delta = ((curr - prev) / prev) * 100;
                    if (Math.abs(delta) < 1e-9) {
                        return '<span style="margin-left:5px;">0</span>';
                    }
                    if (delta > 0) {
                        return `<span style="margin-left:5px;color:green">▲ ${fmtPct(delta)}</span>`;
                    }
                    return `<span style="margin-left:5px;color:red">▼ ${fmtPct(Math.abs(delta))}</span>`;
                };

                // 2) Oś X z nazw produktów (zachowujemy kolejność – zwykle main na górze)
                const xAxisData = safeRows.map(row => row.name);

                // 3) Dane świec: [open, close, lowest, highest]
                //    open  -> priceRangeMin   (po usunięciu skrajności)
                //    close -> priceRangeMax   (po usunięciu skrajności)
                //    lowest -> minPrice       (absolutne minimum)
                //    highest -> maxPrice      (absolutne maksimum)
                const seriesData = safeRows.map(row => [row.priceRangeMin, row.priceRangeMax, row.minPrice, row.maxPrice]);

                return {
                    legend: { show: false },
                    tooltip: {
                        trigger: 'item',
                        formatter: params => {
                            const idx = params?.dataIndex ?? -1;
                            const product = safeRows[idx];
                            if (!product) return '';

                            // znajdź rekord porównawczy (po id -> sku -> name)
                            const key = keyOf(product);
                            const prev = key !== undefined && key !== null ? compareMap.get(key) : undefined;

                            const avgTag = diffTag(product.avgPrice, prev?.avgPrice);
                            const minTag = diffTag(product.minPrice, prev?.minPrice);
                            const maxTag = diffTag(product.maxPrice, prev?.maxPrice);

                            return `
                                <div style="display:flex;flex-direction:column;gap:3px;">
                                    <div><span style="font-weight:bold;">${product.name}</span></div>
                                    <div style="display:flex;gap:10px;">
                                        <span style="display:inline-block;width:100px">${this.$t('priceRange')}:</span>
                                        <span style="font-weight:bold;">
                                            ${fc(product.priceRangeMin)} - ${fc(product.priceRangeMax)}
                                        </span>
                                    </div>
                                    <div style="display:flex;gap:10px;">
                                        <span style="display:inline-block;width:100px">${this.$t('avgPrice')}:</span>
                                        <span style="font-weight:bold;">
                                            <span>${fc(product.avgPrice)}</span>
                                            ${avgTag}
                                        </span>
                                    </div>
                                    <div style="display:flex;gap:10px;">
                                        <span style="display:inline-block;width:100px">${this.$t('minPrice')}:</span>
                                        <span style="font-weight:bold;">
                                            <span>${fc(product.minPrice)}</span>
                                            ${minTag}
                                        </span>
                                    </div>
                                    <div style="display:flex;gap:10px;">
                                        <span style="display:inline-block;width:100px">${this.$t('maxPrice')}:</span>
                                        <span style="font-weight:bold;">
                                            <span>${fc(product.maxPrice)}</span>
                                            ${maxTag}
                                        </span>
                                    </div>
                                </div>
                            `;
                        },
                    },
                    xAxis: {
                        type: 'category',
                        data: xAxisData,
                        axisLabel: { rotate: 10 },
                    },
                    yAxis: {
                        type: 'value',
                        name: this.$t('price'),
                    },
                    series: [
                        {
                            name: this.$t('priceRange'),
                            type: 'candlestick',
                            data: seriesData,
                            itemStyle: {
                                color: '#7e22ce',
                                color0: '#7e22ce',
                                borderColor: '#7e22ce',
                                borderColor0: '#7e22ce',
                            },
                        },
                    ],
                };
            },
        },
    };
</script>



<i18n lang="json" locale="pl" scope="local">
{
    "chartHeaderTooltip": "Wykres prezentuje wartości dla ceny minimalnej i maksymalnej oraz odchylenia względem ceny średniej."
}
</i18n>

<i18n lang="json" locale="en" scope="local">
{
    "chartHeaderTooltip": "The chart shows values for the minimum and maximum price, as well as deviations from the average price."
}
</i18n>
